import SimpleSearchField from '@/components/common/SimpleSearchField';
import { useAnalytics } from '@/hooks/useAnalytics';
import { AnalyticsPage, Call, CallTableInfo } from '@/types/analytics';
import { StripedDataGrid } from '@/util/datagrid';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField
} from '@mui/material';
import { GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid-pro';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import { FC, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useSearchParams } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';
import { callListColumns } from '../columns';
import CallListFooter from './CallListFooter';
import ChannelInfo from './ChannelInfo';

type Filter = { category: 'App name' | 'Company' | 'Host'; value: string };

type TimeSetter = '15m' | '1h' | '24h' | 'today' | 'yesterday' | '1w' | '2w' | '1m' | 'custom';

const parseFilterParam = (param: string): Filter | undefined => {
  const separatorIndex = param.indexOf(':');
  if (separatorIndex === -1) return;

  const category = param.slice(0, separatorIndex);
  const value = param.slice(separatorIndex + 1);

  return category === 'App name' || category === 'Company' || category === 'Host' ? { category, value } : undefined;
};

type CallListProps = {
  openLog: (page: AnalyticsPage, newUUID: string) => void;
  selectedRow?: GridRowSelectionModel;
  setSelectedRow: (newRow: GridRowSelectionModel) => void;
};

const CallList: FC<CallListProps> = ({ openLog, selectedRow, setSelectedRow }) => {
  const { getCallList } = useAnalytics();
  const auth = useAuth();

  const [searchParams, setSearchParams] = useSearchParams();

  const [filterList, setFilterList] = useState<Filter[]>([]);
  const [search, setSearch] = useState<string>('');
  const [calls, setCalls] = useState<Call[]>([]);
  const [currentPage, setCurrentPage] = useSessionStorage<number>('CallListCurrentPage', 0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [selectedCall, setSelectedCall] = useState<Call>();
  const [startDateTime, setStartDateTime] = useState<Dayjs | null>(null);
  const [endDateTime, setEndDateTime] = useState<Dayjs | null>(null);
  const [sortModal, setSortModal] = useSessionStorage<GridSortModel>('CallListSort', []);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [timeSetter, setTimeSetter] = useState<TimeSetter>('15m');

  const structureCalls = useMemo(() => {
    const result: CallTableInfo[] = [];

    calls.forEach((call) => {
      const totalDuration = call.channels.reduce((sum, item) => sum + (Number(item.duration) || 0), 0);
      const grouped = call.channels.reduce<Record<string, CallTableInfo>>((acc, log) => {
        const key = log.uuid;

        if (!acc[key]) {
          acc[key] = {
            start: dayjs(log.timestamp).format('YYYY-MM-DD HH:mm:ss'),
            end: dayjs(log.timestamp).add(totalDuration, 'seconds').format('YYYY-MM-DD HH:mm:ss').toString(),
            duration: totalDuration,
            company: '',
            appname: log.appname,
            uuid: log.uuid,
            hostname: log.hostname,
            channels: call.channels
          };
        }

        if (log.timestamp < acc[key].start) {
          acc[key].start = dayjs(log.timestamp).format('YYYY-MM-DD HH:mm:ss');
        }

        return acc;
      }, {});

      result.push(Object.values(grouped)[0]);
    });

    return result.sort((a, b) => dayjs(b.start).valueOf() - dayjs(a.start).valueOf());
  }, [calls]);

  const filteredCalls = useMemo((): CallTableInfo[] => {
    const filterCompanies = filterList.filter((filter) => filter.category === 'Company');
    const filterApps = filterList.filter((filter) => filter.category === 'App name');
    const filterHosts = filterList.filter((filter) => filter.category === 'Host');

    const filterFilter = filterList.length
      ? structureCalls.filter(
          (call) =>
            (!filterCompanies.length || filterCompanies.find((filter) => filter.value === call.company)) &&
            (!filterApps.length || filterApps.find((filter) => filter.value === call.appname)) &&
            (!filterHosts.length || filterHosts.find((filter) => filter.value === call.hostname))
        )
      : structureCalls;

    return filterFilter;
  }, [calls, filterList]);

  const handleSelectedRow = (uuid: string) => {
    const call = calls.find((call) => call.uuid === uuid);
    if (!call) return;

    setSelectedCall(call);

    searchParams.set('rowid', call.uuid);
    setSearchParams(searchParams);
  };

  const handleFilterChange = (newFilters: Filter[]) => {
    searchParams.delete('filter');
    newFilters.forEach((filter) => searchParams.append('filter', `${filter.category}:${filter.value}`));
    setSearchParams(searchParams);

    setFilterList(newFilters);
  };

  const handleCloseDialog = () => {
    setSelectedCall(undefined);

    searchParams.delete('rowid');
    setSearchParams(searchParams);
  };

  const getTimeSetterValue = (value: TimeSetter): { start: Dayjs | null; end: Dayjs | null } => {
    switch (value) {
      case '15m':
        return { start: dayjs().subtract(15, 'minutes'), end: null };
      case '1h':
        return { start: dayjs().subtract(1, 'hour'), end: null };
      case '24h':
        return { start: dayjs().subtract(1, 'day'), end: null };
      case 'today':
        return { start: dayjs().startOf('day'), end: null };
      case 'yesterday':
        return { start: dayjs().startOf('day').subtract(1, 'day'), end: dayjs().endOf('day').subtract(1, 'day') };
      case '1w':
        return { start: dayjs().startOf('day').subtract(1, 'week'), end: null };
      case '2w':
        return { start: dayjs().startOf('day').subtract(2, 'weeks'), end: null };
      case '1m':
        return { start: dayjs().startOf('day').subtract(1, 'month'), end: null };
      default:
        return { start: startDateTime ?? null, end: endDateTime ?? null };
    }
  };

  const handleTimeSetter = (value: TimeSetter) => {
    setTimeSetter(value);
    setStartDateTime(null);
    setEndDateTime(null);
  };

  const getFilterOptions = useMemo(() => {
    const appnames = [...new Set(structureCalls.map((call) => call.appname))];
    const companies = [...new Set(structureCalls.map((call) => call.company))];
    const hosts = [...new Set(structureCalls.map((call) => call.hostname))];

    const categories: Filter[] = appnames[0] ? appnames.map((value) => ({ category: 'App name', value: value })) : [];

    companies[0] && companies.forEach((value) => categories.push({ category: 'Company', value: value }));
    hosts[0] && hosts.forEach((value) => categories.push({ category: 'Host', value: value }));

    return categories;
  }, [structureCalls]);

  const getCalls = (
    setter: TimeSetter,
    searchValue: string,
    page?: number,
    sessionStart?: Dayjs,
    sessionEnd?: Dayjs
  ) => {
    const { start, end } =
      setter === 'custom' ? { start: startDateTime, end: endDateTime } : getTimeSetterValue(setter);
    setIsLoading(true);
    getCallList.mutate(
      { startDateTime: sessionStart || start, endDateTime: sessionEnd || end, search: searchValue, page: page ?? 0 },
      {
        onSuccess: (callsList) => {
          if (!callsList) return;

          setCalls(callsList.calls);
          setCurrentPage(page ?? 0);
          setTotalPages(callsList.total_pages);

          const id = searchParams.get('rowid');
          if (!id) return;

          const foundId = callsList.calls.find((item) => item.uuid === id);
          if (!foundId) return;

          setSelectedCall(foundId);
        },
        onSettled: () => setIsLoading(false)
      }
    );
  };

  const handlePageChange = (direction: 'left' | 'right') => {
    const page = direction === 'left' ? currentPage - 1 : currentPage + 1;
    getCalls(timeSetter, search, page);
  };

  const handleSearch = () => {
    if (!isValid()) return;

    searchParams.delete('rowid');

    search ? searchParams.set('search', search) : searchParams.delete('search');

    searchParams.set('timeSetter', timeSetter);

    if (timeSetter === 'custom') {
      startDateTime
        ? searchParams.set('start', startDateTime.format('YYYY-MM-DD HH:mm:ss.SSS'))
        : searchParams.delete('start');
      endDateTime ? searchParams.set('end', endDateTime.format('YYYY-MM-DD HH:mm:ss.SSS')) : searchParams.delete('end');
    } else {
      searchParams.delete('start');
      searchParams.delete('end');
    }

    setSearchParams(searchParams);

    getCalls(timeSetter, search);
  };

  const isValid = () => {
    if (startDateTime && endDateTime) {
      return Boolean(
        dayjs(startDateTime).isValid() &&
        dayjs(endDateTime).isValid() &&
        dayjs(startDateTime) < dayjs(endDateTime) &&
        dayjs(startDateTime) < dayjs()
      );
    }
    if (startDateTime) {
      return Boolean(dayjs(startDateTime).isValid() && dayjs(startDateTime) < dayjs());
    }
    if (endDateTime) {
      return Boolean(dayjs(endDateTime).isValid());
    }
    return true;
  };

  useEffect(() => {
    if (!auth.isAuthenticated) return;

    sessionStorage.removeItem('SessionHighlightSub');
    sessionStorage.removeItem('SessionHighlightTask');
    sessionStorage.removeItem('SessionLines');
    sessionStorage.removeItem('SessionTasks');
    sessionStorage.removeItem('SessionLockedSubs');
    sessionStorage.removeItem('SessionMarkedLines');
    sessionStorage.removeItem('SessionSelectedRow');
    sessionStorage.removeItem('SessionScroll');
    sessionStorage.removeItem('AppLogsSort');
    sessionStorage.removeItem('DatabaseSort');

    const setter = (searchParams.get('timeSetter') as TimeSetter) || timeSetter;
    const paramSearch = searchParams.get('search') ?? '';
    const paramFilters: Filter[] = [];

    searchParams.getAll('filter').forEach((param) => {
      const filter = parseFilterParam(param);
      filter && paramFilters.push(filter);
    });

    const paramStart = searchParams.get('start');
    const paramEnd = searchParams.get('end');
    const start = setter === 'custom' && paramStart && dayjs(paramStart).isValid() ? dayjs(paramStart) : undefined;
    const end = setter === 'custom' && paramEnd && dayjs(paramEnd).isValid() ? dayjs(paramEnd) : undefined;

    setSearch(paramSearch);
    setFilterList(paramFilters);

    if (setter === 'custom') {
      start && setStartDateTime(start);
      end && setEndDateTime(end);
      setTimeSetter('custom');
    } else {
      handleTimeSetter(setter);
    }

    getCalls(setter, paramSearch, currentPage, start, end);
  }, [auth.isAuthenticated]);

  return (
    <Stack maxHeight={'90%'} my={2} mx={1}>
      <Stack direction='row' mb={1}>
        <SimpleSearchField
          size='small'
          search={search}
          setSearch={(newSearch) => setSearch(newSearch)}
          onKeyUp={(e) => e.key === 'Enter' && handleSearch()}
          sx={{ width: '25%', mr: 2 }}
        />
        <DateTimePicker
          label='Start Date'
          value={startDateTime}
          onChange={(value) => {
            setTimeSetter('custom');
            setStartDateTime(value);
          }}
          format='YYYY-MM-DD HH:mm'
          ampm={false}
          slotProps={{
            textField: {
              size: 'small',
              onKeyUp: (e) => e.key === 'Enter' && handleSearch()
            },
            field: { clearable: true }
          }}
          disableFuture
          sx={{ mr: 2 }}
        />
        <DateTimePicker
          label='End Date'
          value={endDateTime}
          onChange={(value) => {
            setTimeSetter('custom');
            setEndDateTime(value);
          }}
          minDate={startDateTime || undefined}
          format='YYYY-MM-DD HH:mm'
          ampm={false}
          slotProps={{
            textField: {
              size: 'small',
              onKeyUp: (e) => e.key === 'Enter' && handleSearch()
            },
            field: { clearable: true }
          }}
          sx={{ mr: 2 }}
        />
        <FormControl size='small' sx={{ width: { xs: '100%', sm: '200px' } }}>
          <Select value={timeSetter} onChange={(e) => handleTimeSetter(e.target.value)} size='small'>
            <MenuItem value='15m'>15 minutes</MenuItem>
            <MenuItem value='1h'>1 hour</MenuItem>
            <MenuItem value='24h'>24 hours</MenuItem>
            <MenuItem value='today'>Today</MenuItem>
            <MenuItem value='yesterday'>Yesterday</MenuItem>
            <MenuItem value='1w'>1 week</MenuItem>
            <MenuItem value='2w'>2 weeks</MenuItem>
            <MenuItem value='1m'>1 month</MenuItem>
            <MenuItem value='custom'>Custom</MenuItem>
          </Select>
        </FormControl>
        <Button variant='contained' disabled={!isValid()} onClick={handleSearch} sx={{ ml: 2 }}>
          Search
        </Button>
      </Stack>
      <Box mb={1}>
        <Autocomplete
          autoFocus
          multiple
          size='small'
          limitTags={2}
          disableCloseOnSelect
          value={filterList}
          onChange={(_, newValue) => handleFilterChange(newValue)}
          options={getFilterOptions}
          isOptionEqualToValue={(option, value) => option.category === value.category && option.value === value.value}
          getOptionLabel={(option) => option.value}
          groupBy={(option) => option.category}
          renderOption={(props, option, { selected }) => (
            <ListItem {...props}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon />}
                checkedIcon={<CheckBoxIcon />}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              <ListItemText>{option.value}</ListItemText>
            </ListItem>
          )}
          renderInput={(params) => <TextField {...params} placeholder='Filter' />}
          sx={{ width: '500px' }}
        />
      </Box>
      {isLoading ? (
        <CircularProgress color='inherit' />
      ) : (
        <StripedDataGrid
          columns={callListColumns((page, uuid) => openLog(page, uuid))}
          rows={filteredCalls}
          getRowId={(row) => row.uuid}
          localeText={{ noRowsLabel: 'No calls available' }}
          sortModel={sortModal}
          onSortModelChange={(newSort) => setSortModal(newSort)}
          rowSelectionModel={selectedRow}
          onRowSelectionModelChange={(selectedRow) => selectedRow.ids.size && setSelectedRow(selectedRow)}
          onRowDoubleClick={(params) => {
            const selectedId = params.id as string;

            setSelectedRow({
              type: 'include',
              ids: new Set([selectedId])
            });
            handleSelectedRow(selectedId);
          }}
          disableMultipleRowSelection
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          getRowClassName={(params: any) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
          slots={{
            footer: () => (
              <CallListFooter currentPage={currentPage} totalPages={totalPages} changePage={handlePageChange} />
            )
          }}
          sx={{ '& .MuiDataGrid-cell:focus': { outline: 'none' } }}
        />
      )}
      <ChannelInfo channels={selectedCall?.channels || []} onClose={handleCloseDialog} />
    </Stack>
  );
};

export default CallList;
